import Cookies from 'js-cookie';

export type CookieConsentPreferences = {
  analytics: boolean;
  marketing: boolean;
};

export const COOKIE_CONSENT_STORAGE_KEY = 'smartgates_cookie_consent_v1';

/** Dispatched after preferences are persisted so listeners can load third-party scripts. */
export const COOKIE_CONSENT_CHANGED_EVENT = 'smartgates-cookie-consent';

const STORAGE_KEY = COOKIE_CONSENT_STORAGE_KEY;
const COOKIE_EXPIRES_DAYS = 365;

function cookieSetOptions(): Cookies.CookieAttributes {
  return {
    expires: COOKIE_EXPIRES_DAYS,
    path: '/',
    sameSite: 'strict',
    ...(typeof window !== 'undefined' && window.location.protocol === 'https:' ? { secure: true } : {}),
  };
}

export type StoredCookieConsent = CookieConsentPreferences & {
  savedAt: number;
};

/** Until the visitor saves a choice in the banner or modal, scripts run as fully allowed (opt-out from tracking after explicit reject/settings). */
export const IMPLICIT_COOKIE_CONSENT_FULL: CookieConsentPreferences = {
  analytics: true,
  marketing: true,
};

/** Effective prefs for script loading: saved choice or implicit full consent when nothing is stored yet. */
export function getEffectiveCookieConsentPreferences(): CookieConsentPreferences {
  const stored = readStoredCookieConsent();

  if (stored) {
    return { analytics: stored.analytics, marketing: stored.marketing };
  }

  return IMPLICIT_COOKIE_CONSENT_FULL;
}

export function readStoredCookieConsent(): StoredCookieConsent | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    let raw = Cookies.get(STORAGE_KEY);

    if (!raw) {
      const legacy = window.localStorage.getItem(STORAGE_KEY);

      if (!legacy) {
        return null;
      }

      const legacyParsed = JSON.parse(legacy) as StoredCookieConsent;

      if (typeof legacyParsed.analytics !== 'boolean' || typeof legacyParsed.marketing !== 'boolean') {
        return null;
      }

      Cookies.set(STORAGE_KEY, legacy, cookieSetOptions());

      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }

      raw = legacy;
    }

    const parsed = JSON.parse(raw) as StoredCookieConsent;

    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function persistCookieConsent(prefs: CookieConsentPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }

  const payload: StoredCookieConsent = {
    ...prefs,
    savedAt: Date.now(),
  };
  Cookies.set(STORAGE_KEY, JSON.stringify(payload), cookieSetOptions());

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function dispatchCookieConsentChanged(prefs: CookieConsentPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: prefs }));
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Shared Consent Mode fields for `gtag('consent', 'default' | 'update', ...)`. */
export function buildGtagConsentFields(prefs: CookieConsentPreferences): {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
} {
  const analyticsGranted = prefs.analytics ? 'granted' : 'denied';
  const adsGranted = prefs.marketing ? 'granted' : 'denied';

  return {
    analytics_storage: analyticsGranted,
    ad_storage: adsGranted,
    ad_user_data: adsGranted,
    ad_personalization: adsGranted,
  };
}

/** Sends consent mode updates when `gtag` exists (after optional inject in `ThirdPartyScripts`). */
export function pushConsentToGtag(prefs: CookieConsentPreferences): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('consent', 'update', buildGtagConsentFields(prefs));
}

export function isCookieConsentPreferences(value: unknown): value is CookieConsentPreferences {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const v = value as Record<string, unknown>;

  return typeof v.analytics === 'boolean' && typeof v.marketing === 'boolean';
}
